import React, { memo, useContext } from 'react'
import { LastLayerContext, useDetectIntersection } from '@cargomart/ui'
import { FloatingFocusManager, useDismiss, useFloating, useInteractions } from '@floating-ui/react'
import cn from 'classnames'
import { stopBubbling } from 'src/utils/event'

export type TSize = 'sm' | 'nm' | 'lg' | 'xl' | 'full'

export const ModalContext = React.createContext<{
  isStickyFooter?: boolean
  isStickyHeader?: boolean
  onClose?: () => void
}>({})

export interface IModalProps {
  children: React.ReactNode
  className?: string
  /** Слот для верхнего блока диалога */
  id?: string
  /** Закрытие окна по нажатию на Esc */
  isCloseOnEsc?: boolean
  /** Закрытие окна по клику вне области этого окна */
  isCloseOnOutsideClick?: boolean
  isDialogType?: boolean
  isMobile?: boolean
  onClose?: () => void
  side?: 'left' | 'right'
  /** Размер окна */
  size?: TSize
  testId?: string
}

const sizes = {
  sm: ' max-w-[448px]',
  nm: 'max-w-[600px]',
  lg: 'max-w-[844px]',
  xl: 'max-w-[1200px]',
  full: 'max-w-[calc(100vw_-_4rem)]'
} as const

export const Modal = memo(function Modal({
  id,
  isCloseOnOutsideClick = true,
  isCloseOnEsc = true,
  children,
  size = 'nm',
  onClose,
  className,
  isDialogType,
  side,
  isMobile,
  testId
}: IModalProps) {
  const { context, refs } = useFloating({
    open: true,
    onOpenChange: onClose
  })
  const isLastLayer = useContext(LastLayerContext)

  const [isStickyHeader, refTopSentinel] = useDetectIntersection()
  const [isStickyFooter, refBottomSentinel] = useDetectIntersection()

  const dismiss = useDismiss(context, {
    escapeKey: isCloseOnEsc && isLastLayer,
    outsidePress: (event) => {
      if ((event.target as HTMLElement)?.closest('[data-test="notification"]')) {
        return false
      }

      return isCloseOnOutsideClick && isLastLayer
    }
  })

  const { getFloatingProps } = useInteractions([dismiss])

  const preparedSide = isMobile ? '' : side

  const cls = cn(
    isMobile && `flex flex-col w-full outline-hidden ${isDialogType ? 'justify-end' : 'min-h-screen h-full'}`,
    !isMobile && !preparedSide && 'rounded-xl shadow-5',
    preparedSide && 'min-h-screen',
    preparedSide === 'left' && 'mr-auto',
    preparedSide === 'right' && 'ml-auto'
  )

  return (
    <div
      onClick={stopBubbling}
      className={cn(
        'relative z-modal h-full min-h-screen w-full',
        isDialogType && 'flex items-center',
        isDialogType && isMobile && 'items-end!'
      )}
    >
      <ModalContext.Provider value={{ isStickyHeader, isStickyFooter, onClose }}>
        <FloatingFocusManager context={context} order={['floating', 'content']} returnFocus={false}>
          <div className={cn(!isMobile && !preparedSide && 'flex !h-max w-full flex-col items-center px-6 py-12')}>
            <div className="relative">
              <div ref={refTopSentinel} className="absolute -top-px h-px bg-negative" />
            </div>
            <div
              ref={refs.setFloating}
              {...getFloatingProps()}
              className={cn('outline-hidden relative w-full bg-surface', cls, !isMobile && sizes[size], className)}
              data-test-id={testId}
              id={id}
            >
              {children}
            </div>
            <div className="relative">
              <div ref={refBottomSentinel} className="absolute -bottom-px h-px bg-negative" />
            </div>
          </div>
        </FloatingFocusManager>
      </ModalContext.Provider>
    </div>
  )
})
